// ABOUTME: Sends authenticated frontend requests through the Compleo API.
// ABOUTME: Records request phases without changing the result returned to callers.
import axios, { AxiosResponse } from "axios";
import { Auth } from "aws-amplify";
import pako from "pako";
import {
    isTrackedApiEndpoint,
    isTrackedClientRoute,
    NetworkFailureReason
} from "observability/performanceLogger";
import {
    observeRequestResourceTiming,
    reportApiRequestTiming,
    withIngressCorrelation
} from "./apiTiming";

// How long to wait for a backend response before surfacing an error to the user.
// Keeps the browser from showing a spinner indefinitely when the server hangs.
const REQUEST_TIMEOUT_MS = 60_000;

// CV parsing can legitimately take longer than the default request timeout,
// especially when the parser service needs document conversion first.
export const PARSER_DIRECT_TIMEOUT_MS = 300_000;

export const apiDirectCall = async (
    endpoint: string,
    verb: AxiosType = "get",
    params: Compleo.IObject = {},
    options: Compleo.IObject = {},
    fullUrl: boolean = false,
    addAuth: boolean = true
) => {
    const initialEndPoint = import.meta.env.VITE_API_ADDRESS;
    const requestStartedAt = performance.now();
    const requestRoutePath = getCurrentRoutePath();
    const observabilityOptions = options.observability || {};
    const shouldLogPerformance =
        observabilityOptions.disablePerformanceLog !== true;
    const finalOptions = { ...options };
    const shouldCorrelateWithIngress =
        shouldLogPerformance &&
        !fullUrl &&
        (isTrackedClientRoute(requestRoutePath) ||
            isTrackedApiEndpoint(endpoint));

    delete finalOptions.observability;

    let optionsRequest: Compleo.IObject = {};
    let authDurationMs = 0;
    if (addAuth) {
        const authStartedAt = performance.now();
        const authHeader = await getAmplifyAuthHeader();
        authDurationMs = performance.now() - authStartedAt;
        optionsRequest.headers = {
            ...(finalOptions.headers || {}),
            ...authHeader
        };
        optionsRequest = {
            timeout: REQUEST_TIMEOUT_MS,
            ...finalOptions,
            ...optionsRequest
        };
    } else {
        optionsRequest = { timeout: REQUEST_TIMEOUT_MS, ...finalOptions };
    }

    let endPointLocal = initialEndPoint + endpoint;
    let requestId: string | undefined;
    if (fullUrl) {
        endPointLocal = endpoint;
    } else if (shouldCorrelateWithIngress) {
        const correlated = withIngressCorrelation(endPointLocal);
        endPointLocal = correlated.url;
        requestId = correlated.requestId;
        if (requestId) observeRequestResourceTiming(requestId);
    }

    let response: AxiosResponse | undefined;
    let requestError: any = null;
    const transportStartedAt = performance.now();
    const transportStartedAtEpochMs = Date.now();
    let transportFinishedAt: number | undefined;

    try {
        let result: AxiosResponse;
        if (verb === "get") {
            result = await axios.get(endPointLocal, optionsRequest);
        } else if (verb === "post") {
            result = await axios.post(endPointLocal, params, optionsRequest);
        } else {
            result = await axios.put(endPointLocal, params, optionsRequest);
        }

        response = result;
        transportFinishedAt = performance.now();
        return handleCompressedResultGzip(result);
    } catch (error) {
        transportFinishedAt ??= performance.now();
        requestError = error;
        throw error;
    } finally {
        if (shouldLogPerformance) {
            const completedAt = performance.now();
            void reportApiRequestTiming({
                routePath: requestRoutePath,
                endpoint,
                verb,
                statusCode: response?.status || requestError?.response?.status,
                fullUrl,
                errorMessage: requestError?.message,
                networkFailureReason:
                    requestError?.request && !response && !requestError.response
                        ? classifyNetworkFailure(requestError.code)
                        : undefined,
                requestId,
                startedAt: requestStartedAt,
                completedAt,
                authDurationMs,
                transportStartedAt,
                transportFinishedAt: transportFinishedAt ?? completedAt,
                transportStartedAtEpochMs
            });
        }
    }
};

const classifyNetworkFailure = (code: unknown): NetworkFailureReason => {
    if (code === "ECONNABORTED" || code === "ETIMEDOUT") return "timeout";
    if (code === "ERR_CANCELED") return "aborted";
    return "network_error";
};

const getCurrentRoutePath = () => {
    try {
        return window.location.pathname || "/";
    } catch {
        return "/";
    }
};

const handleCompressedResultGzip = (response: AxiosResponse) => {
    if (Object.keys(response.data || {}).includes("compressedData")) {
        const binaryString = atob(response.data.compressedData);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        // Decompress using pako
        const decompressedData = pako.inflate(bytes, { to: "string" });
        response.data = JSON.parse(decompressedData);
    }
    return response;
};

const getAmplifyAuthHeader = async () => {
    try {
        return {
            Authorization: (await Auth.currentSession())
                .getIdToken()
                .getJwtToken()
        };
    } catch (e) {
        return {};
    }
};

export type AxiosType = "get" | "post" | "put";
